/* global Plaid */
import { debounce } from 'es-toolkit';
import React, { useCallback, useState } from 'react';
import type {
  PlaidLinkError,
  PlaidLinkOnEvent,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccess,
  PlaidLinkOptions,
} from 'react-plaid-link';
import { PlaidLinkStableEvent, usePlaidLink } from 'react-plaid-link';

import { Button as ConstellationButton, TextButton } from '@zillow/constellation-10';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import { DEBOUNCE_WAIT_TIME } from '@common/constants';
import { logError } from '@zg-rentals/log-error';
import { getGlobalLogger } from '@zg-rentals/logger-browser';

const logger = getGlobalLogger('rent-payment-web-plaid');

interface IPlaidButtonProps {
  plaidEnv: string;
  plaidLinkToken?: string;
  plaidLinkTokenExpiration?: number;
  label: string;
  IconComponent?: any;
  icon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  emphasis?: 'filled' | 'outlined' | 'bare';
  dataTest?: string;
  onSuccess: (plaidToken: Record<string, unknown>) => any;
  onExit: (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => any;
  onClickTracking?: () => void;
  getPlaidLinkToken: () => any;
  useConstellationButton?: boolean;
  full?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onClickForStorybook?: () => void;
  variant?: 'button' | 'text';
  userZuid?: string;
}

const PlaidButton: React.FC<React.PropsWithChildren<IPlaidButtonProps>> = (props: IPlaidButtonProps): JSX.Element => {
  const [token, setToken] = useState<string | null>(props.plaidLinkToken || '');

  const createLinkToken = async () => {
    const response = await props.getPlaidLinkToken();
    setToken(response?.payload?.data?.plaidLinkToken);
  };

  // get link_token from your server when component mounts
  React.useEffect(() => {
    const { plaidEnv, plaidLinkToken, plaidLinkTokenExpiration } = props;
    if (!plaidEnv) {
      throw new Error(`Invalid plaid config: ${plaidEnv}`);
    }
    if (!plaidLinkToken || (plaidLinkTokenExpiration && plaidLinkTokenExpiration < new Date().getTime())) {
      createLinkToken();
    }

    return () => {
      const reset = () => {
        document.body.style.overflow = '';
      };

      reset();
      // reset style again in case Plaid rewrites it
      setTimeout(reset, 0);
    };
  }, []);

  const { userZuid } = props;

  const onSuccess = useCallback<PlaidLinkOnSuccess>((publicToken, metadata) => {
    // send public_token to your server
    // https://plaid.com/docs/api/tokens/#token-exchange-flow

    const account = metadata.accounts?.[0];
    props.onSuccess({
      publicToken,
      accountId: account?.id ?? '',
      bankName: metadata?.institution?.name || account?.name?.split('.')[0]?.trim() || '',
      mask: account?.mask || '',
      status: (account?.verification_status as string | null | undefined)?.toUpperCase() || '',
    });
  }, []);

  const onExit = async (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
    if (error != null) {
      logger.warn(
        {
          user_zuid: userZuid,
          link_session_id: metadata.link_session_id,
          request_id: metadata.request_id,
          institution_name: metadata.institution?.name,
          exit_status: metadata.status,
          error_code: error.error_code,
          error_type: error.error_type,
          error_message: error.error_message,
        },
        'plaid_link_exit_error',
      );

      if (error.error_code === 'INVALID_LINK_TOKEN') {
        createLinkToken();
      }
    }
    props.onExit(error, metadata);
  };

  const onEvent = useCallback<PlaidLinkOnEvent>(
    (eventName, metadata) => {
      if (eventName !== PlaidLinkStableEvent.ERROR) {
        return;
      }
      logger.warn(
        {
          user_zuid: userZuid,
          link_session_id: metadata.link_session_id,
          request_id: metadata.request_id,
          view_name: metadata.view_name,
          institution_id: metadata.institution_id,
          institution_name: metadata.institution_name,
          error_code: metadata.error_code,
          error_type: metadata.error_type,
          error_message: metadata.error_message,
          timestamp: metadata.timestamp,
        },
        'plaid_link_error_event',
      );
    },
    [userZuid],
  );

  const config: PlaidLinkOptions = {
    onSuccess,
    onExit,
    onEvent,
    token,
  };

  const { open, ready, error } = usePlaidLink(config);

  if (error) {
    logError({ error, context: 'Could not open Plaid window.' });
  }

  const openPlaid = debounce(
    function () {
      if (props.onClickForStorybook) {
        props.onClickForStorybook();
      }
      if (props.onClickTracking) {
        props.onClickTracking();
      }
      open();
    },
    DEBOUNCE_WAIT_TIME,
    { edges: ['leading'] },
  );

  const { label, IconComponent, size, useConstellationButton = false, loading, disabled, variant = 'button' } = props;
  const isDisabled = !ready || Boolean(disabled);

  if (useConstellationButton) {
    if (variant === 'text') {
      return (
        <TextButton disabled={isDisabled} onClick={openPlaid}>
          {label}
        </TextButton>
      );
    }
    return (
      <ConstellationButton
        loading={loading}
        icon={props.icon || IconComponent}
        emphasis={props.emphasis || 'filled'}
        disabled={isDisabled}
        onClick={openPlaid}
        size={size || 'md'}
        fluid={true}
      >
        {label}
      </ConstellationButton>
    );
  }
  return (
    <Button
      type="primary"
      disabled={isDisabled}
      onClick={openPlaid}
      icon={IconComponent}
      dataTest={props.dataTest}
      size={size || 'md'}
      full={props.full ?? true}
    >
      {label}
    </Button>
  );
};

export default PlaidButton;
